import { LIMITI_ALLEGATI, type TipoAllegato } from '@masamune/shared';

/**
 * Le parole e i conti degli allegati della chat di MUNE (29/10/2026, fetta 2F «Allegati» della
 * Fase 2; os-planning/ai/fase-2f-allegati.md, «Parole a schermo», «Scelte» 5 e «Interfaccia»).
 * Qui tutte le frasi che il web scrive sugli allegati, coi numeri presi da LIMITI_ALLEGATI e mai
 * scritti a mano, e le funzioni pure che le scelgono: i nomi dati dall'app («Schermata delle
 * 12:53», « (2)»), il tipo di un file, i MB con la virgola, le misure di una foto lette
 * dall'intestazione prima di aprirla.
 *
 * Le frasi del server non hanno mai il nome del file: il gestore degli errori le scrive nel log
 * («Errori, codici e frasi»). Quelle col nome le compone il web, da `code` e `details`, col nome
 * che il pezzo ha nella striscia; la frase del server si usa solo per i codici che qui non si
 * conoscono.
 *
 * Importa solo @masamune/shared, mai lib/api.ts né gli store: il test
 * (apps/web/test/allegati-testo.test.ts) lo lancia tsx da apps/api, dove import.meta.env non c'è
 * (lib/api.ts lo legge all'import). Niente JSX e niente DOM quando si importa.
 */

// ── I tetti in byte ──────────────────────────────────────────────────────────

/** Un MB come lo conta il server per i tetti dei file: 1024 × 1024 byte. */
export const BYTE_MB = 1024 * 1024;
/** La foto ridisegnata che parte verso il server: al massimo 1 MB. */
export const BYTE_FOTO_PRONTA = LIMITI_ALLEGATI.kbFoto * 1024;
/** La foto scelta, prima di aprirla: oltre 30 MB non si apre nemmeno (Safari e la memoria dei canvas). */
export const BYTE_ORIGINALE = LIMITI_ALLEGATI.mbOriginale * BYTE_MB;
/** Un PDF: al massimo 5 MB, che in base64 sono 6,7 MB. */
export const BYTE_PDF = LIMITI_ALLEGATI.mbPdf * BYTE_MB;
/** La miniatura da 160 px che va col file: al massimo 12 KB. */
export const BYTE_MINIATURA = LIMITI_ALLEGATI.kbMiniatura * 1024;
/** Oltre 50 milioni di pixel la foto non si apre: un PNG bianco da 30.000 × 30.000 pesa 3 MB e aperto ne vuole 3,6 GB. */
export const PIXEL_MASSIMI = LIMITI_ALLEGATI.megapixel * 1_000_000;

/**
 * Cosa si può scegliere con la graffetta. Al computer i formati che il browser sa ridisegnare
 * (HEIC no: Edge e Chrome su Windows non lo aprono); al telefono `image/*`, così iOS mostra
 * «Libreria foto», «Scatta foto» e «Scegli file» e dalla Libreria consegna già i JPEG. Mai
 * `capture`: aprirebbe subito la fotocamera invece della scelta.
 */
export const ACCETTA_COMPUTER = 'image/jpeg,image/png,image/webp,application/pdf';
export const ACCETTA_TELEFONO = 'image/*,application/pdf';

// ── Le parole ────────────────────────────────────────────────────────────────

/** Le frasi fisse di «Parole a schermo» (fase-2f-allegati.md). Quelle col nome sono le funzioni sotto. */
export const PAROLE_ALLEGATI = {
  graffetta: 'Allega foto o PDF',
  rilascio: 'Lascia qui le foto e i PDF da far leggere a MUNE',
  preparoFoto: 'Preparo la foto…',
  caricoFoto: 'Carico la foto…',
  caricoPdf: 'Carico il PDF…',
  /** Il PDF di cui il server non ha trovato le pagine. */
  pdfSenzaPagine: 'PDF',
  nonLetto: 'Non letto',
  riprova: 'Riprova',
  nuovaConversazione: 'Nuova conversazione',
  finiscoFoto: 'Finisco di caricare la foto…',
  finiscoPdf: 'Finisco di caricare il PDF…',
  finiscoAllegati: 'Finisco di caricare gli allegati…',
  /** ALLEGATO_SPARITO quando nessun pezzo ha l'id di `details.allegati`. */
  sparito: "Un allegato non c'è più: toglilo e allegalo di nuovo.",
  /** ALLEGATI_CONVERSAZIONE: la riga rossa ha accanto il tasto «Nuova conversazione». */
  troppiInConversazione: 'In questa conversazione ci sono già troppi allegati: aprine una nuova per mandarne altri.',
  /**
   * ALLEGATI_TROPPO_PIENI (29/10/2026, dalla revisione della 2F): gli allegati della domanda da soli
   * passano i tetti, anche in una conversazione nuova. Niente «Nuova conversazione»: non basterebbe.
   * Il bordo rosso va sui più pesanti. Frase nuova, da far vedere a Lorenzo.
   */
  troppiInsieme: 'Questi allegati insieme sono troppi per una domanda: mandane meno per volta.',
  chiudiComputer: 'Chiudi (Esc)',
  chiudiTelefono: 'Chiudi',
  /** Il pannello vuoto (AssistentePanel.tsx), con la frase nuova della 2F in coda. */
  vuoto: 'Chiedimi di clienti, progetti, task, trattative e soldi: guardo nel gestionale e ti rispondo coi dati. Puoi anche incollarmi un testo da riscrivere o dividere, o mandarmi una foto o un PDF.',
} as const;

/** I nomi tra virgolette dritte, come in tutte le frasi della tabella. */
const tra = (nome: string) => `"${nome}"`;

const INTERO = new Intl.NumberFormat('it-IT', { maximumFractionDigits: 0 });
const DECIMALE = new Intl.NumberFormat('it-IT', { maximumFractionDigits: 1, useGrouping: false });

/**
 * «8,2 MB», «42 MB»: un decimale con la virgola, senza «,0». Con `tetto`, un file appena sopra
 * il tetto si arrotonda per eccesso: «pesa 5 MB: fino a 5 MB» non si capirebbe.
 */
export function mb(byte: number, tetto?: number): string {
  const valore = byte / BYTE_MB;
  let decimi = Math.round(valore * 10);
  if (tetto !== undefined && valore > tetto && decimi <= tetto * 10) decimi = Math.ceil(valore * 10);
  return `${DECIMALE.format(decimi / 10)} MB`;
}

/** «1 pagina», «12 pagine»; «PDF» quando il server non le ha trovate. */
export function pagineDelPdf(pagine: number | null): string {
  if (pagine === null) return PAROLE_ALLEGATI.pdfSenzaPagine;
  return pagine === 1 ? '1 pagina' : `${INTERO.format(pagine)} pagine`;
}

/** La x di ogni pezzo: `title` e `aria-label`. */
export const togliNome = (nome: string) => `Togli ${tra(nome)}`;
/** L'`alt` di ogni miniatura. */
export const altFoto = (nome: string) => `Foto: ${nome}`;

/** Il `title` di un pezzo della striscia: cosa sta facendo, o il suo nome intero quando è pronto. */
export function titoloPezzo(a: { stato: 'preparo' | 'carico' | 'pronto' | 'errore'; tipo: TipoAllegato; nome: string }): string {
  if (a.stato === 'preparo') return PAROLE_ALLEGATI.preparoFoto;
  if (a.stato === 'carico') return a.tipo === 'pdf' ? PAROLE_ALLEGATI.caricoPdf : PAROLE_ALLEGATI.caricoFoto;
  return a.nome;
}

/** Per i lettori di schermo, quando il file è sul server: «Foto "x" allegata», «PDF "x" allegato: 12 pagine». */
export function annuncioPronto(a: { tipo: TipoAllegato; nome: string; pagine: number | null }): string {
  if (a.tipo === 'foto') return `Foto ${tra(a.nome)} allegata`;
  return a.pagine === null ? `PDF ${tra(a.nome)} allegato` : `PDF ${tra(a.nome)} allegato: ${pagineDelPdf(a.pagine)}`;
}

/** Tipo sbagliato. Al telefono anche l'HEIC, che lì si apre. */
export function fraseTipo(nome: string, mobile: boolean): string {
  return mobile
    ? `${tra(nome)}: MUNE legge le foto JPEG, PNG, WebP e HEIC e i PDF.`
    : `${tra(nome)}: MUNE legge le foto JPEG, PNG e WebP e i PDF.`;
}

/** Un HEIC al computer: Edge e Chrome su Windows non lo aprono senza una libreria (scelta 15). */
export const fraseHeicComputer = (nome: string) =>
  `${tra(nome)}: dal computer le foto HEIC non si aprono. Mandala dal telefono o salvala in JPEG.`;
/** Un HEIC che nemmeno il telefono riesce ad aprire. */
export const fraseHeicTelefono = (nome: string) => `${tra(nome)} non si apre: salvala in JPEG e riprova.`;

/** I file oltre il quinto, lasciati fuori: uno, due, o il primo «e altri N file». */
export function fraseTroppi(nomi: readonly string[]): string {
  const coda = `al massimo ${LIMITI_ALLEGATI.perDomanda} per domanda.`;
  const [primo = '', secondo = ''] = nomi;
  if (nomi.length <= 1) return `${tra(primo)} non l'ho allegato: ${coda}`;
  if (nomi.length === 2) return `${tra(primo)} e ${tra(secondo)} non li ho allegati: ${coda}`;
  return `${tra(primo)} e altri ${INTERO.format(nomi.length - 1)} file non li ho allegati: ${coda}`;
}

/** Oltre 50 milioni di pixel, letti dall'intestazione: la foto non si apre. */
export const fraseTroppoGrandeDaAprire = (nome: string) =>
  `${tra(nome)} è troppo grande da aprire: salvala più piccola e riprova.`;

/** La foto scelta oltre i 30 MB: il browser non la apre. */
export const frasePesoFoto = (nome: string, byte: number) =>
  `${tra(nome)} pesa ${mb(byte, LIMITI_ALLEGATI.mbOriginale)}: MUNE apre foto fino a ${LIMITI_ALLEGATI.mbOriginale} MB.`;
/** Il PDF oltre i 5 MB. */
export const frasePesoPdf = (nome: string, byte: number) =>
  `${tra(nome)} pesa ${mb(byte, LIMITI_ALLEGATI.mbPdf)}: MUNE legge PDF fino a ${LIMITI_ALLEGATI.mbPdf} MB.`;

/** ALLEGATO_PAGINE, con `details.pagine`. */
export const frasePagine = (nome: string, pagine: number) =>
  `${tra(nome)} ha ${INTERO.format(pagine)} pagine: MUNE ne legge al massimo ${LIMITI_ALLEGATI.paginePdf}. Mandami solo quelle che servono.`;

/** ALLEGATO_PASSWORD: Anthropic non legge i PDF con la password. */
export const frasePassword = (nome: string) => `${tra(nome)} ha una password: MUNE non lo può aprire.`;

/** Una foto che il browser non apre, o ALLEGATO_ROVINATO dal server. */
export const fraseRovinato = (nome: string, tipo: TipoAllegato) =>
  `${tra(nome)} non si apre: forse è ${tipo === 'pdf' ? 'rovinato' : 'rovinata'}.`;

/** Il lato lungo oltre 4 volte il corto: a 1568 px una pagina web catturata intera non si legge. */
export const fraseLungaStretta = (nome: string) =>
  `${tra(nome)} è troppo lunga e stretta: così non si legge. Mandala a pezzi.`;

/** La rete che cade durante il caricamento: il pezzo resta col bordo rosso e accanto c'è «Riprova». */
export const fraseRete = (nome: string) => `${tra(nome)} non si è caricato.`;

/** Un 413 senza JSON: la pagina di nginx, che ha un tetto più basso dell'API (Da confermare, 6). */
export const fraseNginx = (nome: string) => `${tra(nome)} è troppo grande per il server: prova con un file più piccolo.`;

/** Invia con un pezzo in errore di rete: non parte niente. */
export const fraseInviaConErrore = (nome: string) => `Togli ${tra(nome)} o riprova a caricarlo: poi mando la domanda.`;

/** ALLEGATO_SPARITO col pezzo che ha quell'id. */
export const fraseSparito = (nome: string, tipo: TipoAllegato) =>
  tipo === 'pdf'
    ? `${tra(nome)} non c'è più: toglilo e allegalo di nuovo.`
    : `${tra(nome)} non c'è più: toglila e allegala di nuovo.`;

/** ALLEGATO_TROPPO_PIENO: un PDF da solo oltre i 20.000 token. */
export const fraseTroppoPieno = (nome: string) => `${tra(nome)} è troppo pieno per MUNE: mandami solo le pagine che servono.`;

/** ALLEGATO_NON_LETTO: il conteggio dei token di Anthropic rifiuta il PDF. */
export const fraseNonSiLegge = (nome: string) => `${tra(nome)} non si legge: MUNE non riesce ad aprirlo.`;

/**
 * La riga di stato della risposta mentre i caricamenti finiscono (Invia premuto prima della
 * fine): la foto, il PDF, o gli allegati quando ne mancano più d'uno. Null se sono tutti sul
 * server.
 */
export function fraseFinisco(allegati: ReadonlyArray<{ tipo: TipoAllegato; id: string | null }>): string | null {
  const mancano = allegati.filter((a) => a.id === null);
  const [solo] = mancano;
  if (!solo) return null;
  if (mancano.length > 1) return PAROLE_ALLEGATI.finiscoAllegati;
  return solo.tipo === 'pdf' ? PAROLE_ALLEGATI.finiscoPdf : PAROLE_ALLEGATI.finiscoFoto;
}

// ── Gli errori del caricamento e della domanda ───────────────────────────────

/** Un numero dentro `details` dell'errore (`mb`, `pagine`), se c'è. */
function numeroIn(details: unknown, chiave: string): number | null {
  if (!details || typeof details !== 'object') return null;
  const v = (details as Record<string, unknown>)[chiave];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** Gli id di `details.allegati`: i pezzi col bordo rosso. */
export function allegatiDi(details: unknown): string[] {
  if (!details || typeof details !== 'object') return [];
  const v = (details as Record<string, unknown>).allegati;
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}

/** Il caricamento non riuscito, come arriva da POST /ai/allegati: `code` null senza JSON (nginx, la rete). */
export interface ErroreCaricamento {
  status: number;
  code: string | null;
  message: string | null;
  details?: unknown;
}

const CODICI_DEFINITIVI: ReadonlySet<string> = new Set([
  'ALLEGATO_PESO', 'ALLEGATO_TIPO', 'ALLEGATO_MISURE', 'ALLEGATO_ROVINATO', 'ALLEGATO_PASSWORD', 'ALLEGATO_PAGINE',
]);

/**
 * Riprovare non serve: il file è sbagliato (tipo, peso, misure, rovinato, password, pagine) o
 * troppo grande per nginx. Il pezzo esce dalla striscia e resta la frase. Gli altri errori
 * (rete, 5xx, MUNE spento, troppe richieste) lasciano il pezzo col bordo rosso e «Riprova».
 */
export function caricamentoDefinitivo(status: number, code: string | null): boolean {
  return (code !== null && CODICI_DEFINITIVI.has(code)) || status === 413;
}

/**
 * La frase col nome di un caricamento non riuscito, scelta da `code` e `details`; la frase del
 * server (`message`, senza nome) solo per i codici che qui non si conoscono o che dal web non
 * arrivano (ALLEGATO_MISURE: il browser ridisegna sempre a 1568 px).
 */
export function fraseCaricamento(e: ErroreCaricamento, a: { nome: string; tipo: TipoAllegato; byte: number }, mobile: boolean): string {
  switch (e.code) {
    case 'ALLEGATO_TIPO':
      return fraseTipo(a.nome, mobile);
    case 'ALLEGATO_ROVINATO':
      return fraseRovinato(a.nome, a.tipo);
    case 'ALLEGATO_PASSWORD':
      return frasePassword(a.nome);
    case 'ALLEGATO_PAGINE': {
      const pagine = numeroIn(e.details, 'pagine');
      if (pagine !== null) return frasePagine(a.nome, pagine);
      break;
    }
    case 'ALLEGATO_PESO':
      // Il web non manda PDF oltre i 5 MB: se il server li conta diversi, la sua frase.
      if (a.tipo === 'pdf' && a.byte > BYTE_PDF && numeroIn(e.details, 'mb') === LIMITI_ALLEGATI.mbPdf) return frasePesoPdf(a.nome, a.byte);
      break;
    case 'INTERNAL':
      return fraseRete(a.nome);
  }
  if (e.code === null) {
    if (e.status === 413) return fraseNginx(a.nome);
    if (e.status === 0 || e.status >= 500) return fraseRete(a.nome);
  }
  return e.message || fraseRete(a.nome);
}

/**
 * La frase della domanda rimessa nel campo, con ogni errore arrivato prima di `conversazione`:
 * in JSON prima del flusso (ApiError: `code`, `message`, `details`) o come evento `errore` a
 * flusso aperto (`codice`, `frase`, senza `details`). Per i codici degli allegati il nome del
 * pezzo che ha l'id di `details.allegati`; per gli altri la frase dell'API («Sto ancora
 * rispondendo alla domanda di prima», «MUNE non risponde: riprova tra poco»).
 */
export function fraseDomandaRimessa(
  e: { codice: string | null; frase: string; details?: unknown },
  allegati: ReadonlyArray<{ id: string | null; nome: string; tipo: TipoAllegato }>,
): string {
  const ids = allegatiDi(e.details);
  const pezzo = allegati.find((a) => a.id !== null && ids.includes(a.id));
  switch (e.codice) {
    case 'ALLEGATO_SPARITO':
      return pezzo ? fraseSparito(pezzo.nome, pezzo.tipo) : PAROLE_ALLEGATI.sparito;
    case 'ALLEGATI_CONVERSAZIONE':
      return PAROLE_ALLEGATI.troppiInConversazione;
    case 'ALLEGATI_TROPPO_PIENI':
      return PAROLE_ALLEGATI.troppiInsieme;
    case 'ALLEGATO_TROPPO_PIENO':
      return pezzo ? fraseTroppoPieno(pezzo.nome) : e.frase;
    case 'ALLEGATO_NON_LETTO':
      return pezzo ? fraseNonSiLegge(pezzo.nome) : e.frase;
    default:
      return e.frase;
  }
}

/** ALLEGATI_CONVERSAZIONE: accanto alla frase il tasto «Nuova conversazione». */
export const conNuovaConversazione = (codice: string | null | undefined) => codice === 'ALLEGATI_CONVERSAZIONE';

// ── I nomi ───────────────────────────────────────────────────────────────────

/** Da dove arriva un file: la graffetta, Ctrl+V, il trascinamento sul pannello. */
export type OrigineAllegato = 'scelta' | 'incolla' | 'trascina';

const ORA_ROMA = new Intl.DateTimeFormat('it-IT', { timeZone: 'Europe/Rome', hour: 'numeric', minute: '2-digit' });

/** «Schermata delle 12:53» (incollata), «Foto delle 12:53» (dalla fotocamera del telefono). */
export function nomeDallApp(cosa: 'schermata' | 'foto', adesso: Date): string {
  return `${cosa === 'schermata' ? 'Schermata' : 'Foto'} delle ${ORA_ROMA.format(adesso)}`;
}

/** I nomi che non dicono niente: «image.png» di uno screenshot incollato, «image.jpg» della fotocamera di iOS. */
function nomeGenerico(nome: string): boolean {
  return /^image\.(png|jpe?g|gif|webp|heic|heif|bmp|tiff?)$/i.test(nome);
}

/** L'estensione in minuscolo, senza il punto; vuota se non c'è. */
function estensione(nome: string): string {
  const m = /\.([a-z0-9]{1,5})$/i.exec(nome.trim());
  return m?.[1]?.toLowerCase() ?? '';
}

/**
 * Il nome che il pezzo ha nella striscia. Uno screenshot incollato arriva come «image.png»: diventa
 * «Schermata delle 12:53»; una foto scattata dal telefono arriva come «image.jpg»: diventa «Foto
 * delle 12:53». Gli altri restano come sono (al server va anche questo, nel campo `nome`).
 */
export function nomeDaMostrare(f: { name: string; type: string }, origine: OrigineAllegato, adesso: Date): string {
  const nome = f.name.trim();
  const generico = nome === '' || nomeGenerico(nome);
  if (genereDelFile(f) === 'pdf') return nome || 'Documento PDF';
  if (origine === 'incolla' && generico) return nomeDallApp('schermata', adesso);
  if (generico && (origine === 'scelta' || nome === '')) return nomeDallApp('foto', adesso);
  return nome;
}

/**
 * Un nome già usato nella striscia o nella conversazione prende « (2)», « (3)» prima
 * dell'estensione: «Schermata delle 12:53 (2)», «fattura (2).pdf». Un nome che finisce già con
 * « (2)» va avanti da lì, non diventa « (2) (2)».
 */
export function nomeLibero(nome: string, usati: Iterable<string>): string {
  const presi = new Set(usati);
  if (!presi.has(nome)) return nome;
  const est = /\.[a-z0-9]{1,5}$/i.exec(nome)?.[0] ?? '';
  const base = (est && nome.length > est.length ? nome.slice(0, -est.length) : nome).replace(/ \(\d+\)$/, '');
  for (let n = 2; n < 1000; n++) {
    const prova = `${base} (${n})${est && nome.length > est.length ? est : ''}`;
    if (!presi.has(prova)) return prova;
  }
  return nome;
}

// ── Il tipo del file ─────────────────────────────────────────────────────────

/** Cosa diventa un file scelto: una foto da ridisegnare, un HEIC (solo dal telefono), un PDF. */
export type GenereFile = 'foto' | 'heic' | 'pdf';

const TIPI_FOTO: ReadonlySet<string> = new Set(['image/jpeg', 'image/jpg', 'image/pjpeg', 'image/png', 'image/webp']);
const TIPI_HEIC: ReadonlySet<string> = new Set(['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence']);
const EST_FOTO: ReadonlySet<string> = new Set(['jpg', 'jpeg', 'jpe', 'jfif', 'png', 'webp']);
const EST_HEIC: ReadonlySet<string> = new Set(['heic', 'heif', 'hif']);

/**
 * Il tipo dal `type` del browser e, quando manca, dall'estensione: al computer un HEIC arriva
 * spesso col tipo vuoto, e si riconosce da «.heic» o «.heif». Null per tutto il resto (GIF, SVG,
 * Word, ZIP): la frase del tipo. Il server rifà il controllo sui primi byte.
 */
export function genereDelFile(f: { name: string; type: string }): GenereFile | null {
  const tipo = f.type.trim().toLowerCase();
  const est = estensione(f.name);
  if (tipo === 'application/pdf') return 'pdf';
  if (TIPI_FOTO.has(tipo)) return 'foto';
  if (TIPI_HEIC.has(tipo) || EST_HEIC.has(est)) return 'heic';
  if (tipo === '' || tipo === 'application/octet-stream') {
    if (est === 'pdf') return 'pdf';
    if (EST_FOTO.has(est)) return 'foto';
  }
  return null;
}

/**
 * Ctrl+V nel campo. Con del testo negli appunti si incolla il testo come oggi: Excel e Word
 * copiano anche un'immagine delle celle, che si lascia. Senza testo, i file diventano allegati.
 */
export function cosaIncollare(testo: string, file: number): 'testo' | 'file' {
  return testo.trim() === '' && file > 0 ? 'file' : 'testo';
}

// ── Le misure dall'intestazione ──────────────────────────────────────────────

/** Legge `quanti` byte a partire da `da`: con un File, `Blob.slice`, mai il file intero. */
export type LeggiByte = (da: number, quanti: number) => Promise<Uint8Array>;

export interface MisureFoto {
  formato: 'png' | 'jpeg' | 'webp';
  larghezza: number;
  altezza: number;
}

/** Il lettore di un File o di un Blob, a pezzi. */
export function lettoreDaBlob(file: Blob): LeggiByte {
  return async (da, quanti) => new Uint8Array(await file.slice(da, da + quanti).arrayBuffer());
}

const byte = (b: Uint8Array, i: number) => b[i] ?? 0;
const u16be = (b: Uint8Array, i: number) => (byte(b, i) << 8) | byte(b, i + 1);
const u16le = (b: Uint8Array, i: number) => byte(b, i) | (byte(b, i + 1) << 8);
const u24le = (b: Uint8Array, i: number) => byte(b, i) | (byte(b, i + 1) << 8) | (byte(b, i + 2) << 16);
const u32be = (b: Uint8Array, i: number) => ((byte(b, i) << 24) >>> 0) + ((byte(b, i + 1) << 16) | (byte(b, i + 2) << 8) | byte(b, i + 3));
const ascii = (b: Uint8Array, da: number, quanti: number) => String.fromCharCode(...Array.from(b.subarray(da, da + quanti)));

const FIRMA_PNG = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

/**
 * Larghezza e altezza di una foto lette dall'intestazione, prima di aprirla (fase-2f-allegati.md,
 * scelta 5, «Pixel della foto scelta»): come fa il server. PNG dal blocco IHDR; WebP da VP8, VP8L
 * o VP8X; JPEG dal primo SOF, saltando da un segmento all'altro con pochi byte per segmento (un
 * APP1 con l'EXIF può essere lungo 64 KB, e non si legge). Null se non si capisce: la foto si dice
 * rovinata e non si apre.
 */
export async function misureDaIntestazione(leggi: LeggiByte, dimensione: number): Promise<MisureFoto | null> {
  if (dimensione < 4) return null;
  const testa = await leggi(0, Math.min(dimensione, 32));
  if (FIRMA_PNG.every((x, i) => byte(testa, i) === x)) {
    if (testa.length < 24 || ascii(testa, 12, 4) !== 'IHDR') return null;
    return misure('png', u32be(testa, 16), u32be(testa, 20));
  }
  if (testa.length >= 16 && ascii(testa, 0, 4) === 'RIFF' && ascii(testa, 8, 4) === 'WEBP') return misureWebp(testa);
  if (byte(testa, 0) === 0xff && byte(testa, 1) === 0xd8 && byte(testa, 2) === 0xff) return misureJpeg(leggi, dimensione);
  return null;
}

function misure(formato: MisureFoto['formato'], larghezza: number, altezza: number): MisureFoto | null {
  return larghezza > 0 && altezza > 0 ? { formato, larghezza, altezza } : null;
}

function misureWebp(b: Uint8Array): MisureFoto | null {
  const blocco = ascii(b, 12, 4);
  if (blocco === 'VP8X') {
    if (b.length < 30) return null;
    return misure('webp', 1 + u24le(b, 24), 1 + u24le(b, 27));
  }
  if (blocco === 'VP8 ') {
    // Dopo i 3 byte del fotogramma, il codice d'avvio 9D 01 2A e le misure su 14 bit.
    if (b.length < 30 || byte(b, 23) !== 0x9d || byte(b, 24) !== 0x01 || byte(b, 25) !== 0x2a) return null;
    return misure('webp', u16le(b, 26) & 0x3fff, u16le(b, 28) & 0x3fff);
  }
  if (blocco === 'VP8L') {
    // La firma 0x2F, poi larghezza e altezza meno uno, 14 bit l'una.
    if (b.length < 25 || byte(b, 20) !== 0x2f) return null;
    const b1 = byte(b, 22);
    const larghezza = 1 + (((b1 & 0x3f) << 8) | byte(b, 21));
    const altezza = 1 + (((byte(b, 24) & 0x0f) << 10) | (byte(b, 23) << 2) | ((b1 & 0xc0) >> 6));
    return misure('webp', larghezza, altezza);
  }
  return null;
}

/** I marcatori SOF che portano le misure: da FFC0 a FFCF tranne FFC4 (tabelle), FFC8 e FFCC. */
const eSof = (m: number) => m >= 0xc0 && m <= 0xcf && m !== 0xc4 && m !== 0xc8 && m !== 0xcc;

async function misureJpeg(leggi: LeggiByte, dimensione: number): Promise<MisureFoto | null> {
  let pos = 2;
  // Un tetto ai segmenti: un JPEG vero ne ha una ventina prima del SOF.
  for (let giro = 0; giro < 512; giro++) {
    if (pos + 4 > dimensione) return null;
    const b = await leggi(pos, Math.min(16, dimensione - pos));
    if (b.length < 4 || byte(b, 0) !== 0xff) return null;
    const m = byte(b, 1);
    // Un byte di riempimento (FF FF …): il marcatore è quello dopo.
    if (m === 0xff) { pos += 1; continue; }
    // Marcatori senza lunghezza: SOI, TEM, RST0-7.
    if (m === 0xd8 || m === 0x01 || (m >= 0xd0 && m <= 0xd7)) { pos += 2; continue; }
    // La fine del file o l'inizio dei dati senza un SOF: misure non trovate.
    if (m === 0xd9 || m === 0xda) return null;
    const lunghezza = u16be(b, 2);
    if (lunghezza < 2) return null;
    if (eSof(m)) {
      // FF Cn, lunghezza (2), precisione (1), altezza (2), larghezza (2).
      if (b.length < 9) return null;
      return misure('jpeg', u16be(b, 7), u16be(b, 5));
    }
    pos += 2 + lunghezza;
  }
  return null;
}

/** Oltre 50 milioni di pixel: aperta, la foto riempirebbe la memoria della scheda. */
export const troppiPixel = (m: { larghezza: number; altezza: number }) => m.larghezza * m.altezza > PIXEL_MASSIMI;

/** Il lato lungo oltre 4 volte il corto. */
export const lungaEStretta = (m: { larghezza: number; altezza: number }) =>
  Math.max(m.larghezza, m.altezza) > LIMITI_ALLEGATI.lungaStretta * Math.min(m.larghezza, m.altezza);

/** Le misure col lato lungo al massimo di `lato` (1568 per la foto, 160 per la miniatura); mai più grandi dell'originale. */
export function misureRidotte(larghezza: number, altezza: number, lato: number = LIMITI_ALLEGATI.latoFoto): { larghezza: number; altezza: number } {
  const lungo = Math.max(larghezza, altezza);
  if (lungo <= lato) return { larghezza, altezza };
  const k = lato / lungo;
  return { larghezza: Math.max(1, Math.min(lato, Math.round(larghezza * k))), altezza: Math.max(1, Math.min(lato, Math.round(altezza * k))) };
}
